import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { getRandomPair, getSuggestedMatchups } from "./data/characters";
import { useBattleRequest } from "./hooks/useBattleRequest";
import { useAccess } from "./hooks/useAccess";
import { useAuth } from "./context/AuthContext";
import { getEnforcementAddendum } from "./config/enforcementPresentation";
import TipCarousel from "./components/TipCarousel";
import ModerationAddendum from "./components/ModerationAddendum";
import "./App.css";

const MAX_NAME_LENGTH = 100;
const SAFE_CHAR_RE = /[^\p{L}\p{N}\s\-''.()&:!]/gu;

function sanitizeInput(raw) {
  return raw.replace(/<[^>]*>/g, "").replace(SAFE_CHAR_RE, "").replace(/\s{2,}/g, " ");
}

function App() {
  const location = useLocation();
  const { user } = useAuth();
  const prefill = location.state;
  const [char1, setChar1] = useState(prefill?.char1 || "");
  const [char2, setChar2] = useState(prefill?.char2 || "");
  const suggestions = useMemo(() => getSuggestedMatchups(3), []);
  const { retry: retryStanding } = useAccess();
  const { loading, loadingStage, error, errorAction, enforcementPresentation, submit } = useBattleRequest(retryStanding);
  const addendum = getEnforcementAddendum(enforcementPresentation);

  // Restore a matchup that was blocked pending sign-in (see useBattleRequest's
  // auth_required_for_battle handling) once the user actually signs in.
  const wasSignedIn = useRef(Boolean(user));
  useEffect(() => {
    if (user && !wasSignedIn.current) {
      const pending = sessionStorage.getItem("pendingBattle");
      if (pending) {
        try {
          const { char1: pc1, char2: pc2 } = JSON.parse(pending);
          if (pc1) setChar1(pc1);
          if (pc2) setChar2(pc2);
        } catch {
          // malformed/unexpected value — ignore
        }
        sessionStorage.removeItem("pendingBattle");
      }
    }
    wasSignedIn.current = Boolean(user);
  }, [user]);

  const handleSurprise = () => {
    const [a, b] = getRandomPair();
    setChar1(a);
    setChar2(b);
  };

  const handleBattle = () => submit(char1, char2);

  const c1 = char1.trim();
  const c2 = char2.trim();
  const bothReady = c1 && c2;

  return (
    <section className="arena-bg">
      <div className="hero">
        <h1 className="text-hero">
          Who Would <span className="hero-accent">Win</span>
        </h1>
        <p className="tagline">
          Name any two combatants — fighters, forces of nature, civilizations, companies, and more. The AI determines the battle, analyzes every factor, and delivers a definitive verdict.<span className="tagline-cursor" aria-hidden="true" />
        </p>
      </div>

      <section className="arena">
        <div className="slot left-slot">
          <label className="slot-label">Combatant 1</label>
          <input
            className="slot-input"
            value={char1}
            onChange={(e) => setChar1(sanitizeInput(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && handleBattle()}
            placeholder="Any fighter, force, or entity..."
            maxLength={MAX_NAME_LENGTH}
            disabled={loading}
          />
        </div>
        <div className="vs-pulse">VS</div>
        <div className="slot right-slot">
          <label className="slot-label">Combatant 2</label>
          <input
            className="slot-input"
            value={char2}
            onChange={(e) => setChar2(sanitizeInput(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && handleBattle()}
            placeholder="Their opponent..."
            maxLength={MAX_NAME_LENGTH}
            disabled={loading}
          />
        </div>
      </section>

      <button className="fight-btn" onClick={handleBattle} disabled={loading}>
        {loading
          ? "Calculating…"
          : bothReady
          ? `${c1} vs ${c2} — FIGHT!`
          : "FIGHT!"}
      </button>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
          <ModerationAddendum addendum={addendum} />
          {errorAction && (
            <Link to={errorAction.path} className="error-banner-link">{errorAction.label}</Link>
          )}
        </div>
      )}

      {!loading && (
        <div className="suggestions-row">
          <button className="surprise-btn" onClick={handleSurprise}>
            Surprise Me
          </button>
          <div className="matchup-chips">
            {suggestions.map(([a, b]) => (
              <button
                key={`${a}:${b}`}
                className="matchup-chip"
                onClick={() => { setChar1(a); setChar2(b); }}
              >
                {a} vs {b}
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && <TipCarousel />}

      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          <p className="loading-stage-text">{loadingStage}</p>
        </div>
      )}
    </section>
  );
}

export default App;
